import { Fragment, type ComponentType, type ReactNode } from "react";
import type { PageComponentData, PageMetadata } from "~/app/api/pageData";
import { canRenderCMSComponent, renderCMSComponent } from "~/features/cms/componentFactory";
import type { CMSComponentProps } from "~/features/cms/types/props";
import { SmartEditWrapper } from "./SmartEditWrapper";

const REGISTERED_CMS_COMPONENTS: Record<string, string> = {
  CategoryNavigationComponent: "Navigation",
  FooterNavigationComponent: "FooterNavigation",
};

const SHELL_REGISTERED_TYPE_CODES = new Set<string>(["BreadcrumbComponent", ...Object.keys(REGISTERED_CMS_COMPONENTS)]);

export function RegisteredComponent({
  name,
  props,
  required = true,
}: {
  name: string;
  props?: unknown;
  required?: boolean;
}) {
  const Factory = window.SCHA.cmps[name] as unknown as ComponentType<Record<string, unknown>> | undefined;

  if (!Factory) {
    if (required) {
      console.error(`StorefrontShell: Could not find a component registered as ${name}`);
    }
    return null;
  }

  return (
    <Fragment>
      <Factory {...(props as object)} />
    </Fragment>
  );
}

export function canRenderCmsComponentType(typeCode: string): boolean {
  return SHELL_REGISTERED_TYPE_CODES.has(typeCode) || canRenderCMSComponent(typeCode);
}

export function renderCmsSlotComponent(component: PageComponentData, metadata: PageMetadata): ReactNode {
  let content: ReactNode;
  if (component.typeCode === "BreadcrumbComponent") {
    content = metadata.breadcrumbs.length ? (
      <RegisteredComponent name="Breadcrumbs" props={{ items: metadata.breadcrumbs }} required={false} />
    ) : null;
  } else {
    const registeredName = REGISTERED_CMS_COMPONENTS[component.typeCode];
    if (registeredName) {
      content = <RegisteredComponent name={registeredName} props={component.props} required={false} />;
    } else {
      const props = {
        ...component.props,
        type: component.typeCode,
        id: component.uid,
      } as CMSComponentProps;

      content = renderCMSComponent(props);
    }
  }

  return (
    <SmartEditWrapper kind="component" smartEdit={component.smartEdit}>
      {content}
    </SmartEditWrapper>
  );
}
